import { useEffect, useMemo, useState } from 'react';
import API, { fmt } from '@/lib/shopApi';
import { MagnifyingGlass, Plus, Minus, Image as ImageIcon, X } from '@phosphor-icons/react';

const VOORRAAD_DOTS = {
  groen: { color: '#16a34a', label: 'Op voorraad' },
  oranje: { color: '#f59e0b', label: 'Beperkt' },
  rood: { color: '#dc2626', label: 'Op aanvraag' },
};

function Kaart({ a, qty, onAdd, onMin }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const dot = VOORRAAD_DOTS[a.voorraad_badge] || VOORRAAD_DOTS.rood;
  return (
    <div className={`relative bg-white border-2 ${qty > 0 ? 'shop-border-primary shadow-lg' : 'border-gray-200'} flex flex-col`} data-testid={`shop-art-${a.art_code}`}>
      <div className="relative" style={{ paddingTop: '75%', background: '#F3F4F6' }}>
        {a.foto_url ? (
          <img src={`${apiBase}${a.foto_url}`} alt={a.omschrijving} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"><ImageIcon size={40} className="text-gray-300" /></div>
        )}
        <div className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 bg-white/90 px-1.5 py-0.5 text-[10px] font-bold" title={dot.label}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot.color }} />
          {dot.label}
        </div>
      </div>
      <div className="p-2.5 flex-1 flex flex-col">
        <div className="flex items-center gap-1 text-[11px] text-gray-500 font-mono mb-1">
          {qty > 0 && <span className="shop-bg-primary text-white text-[10px] font-bold px-1.5 py-0.5">× {qty}</span>}
          <span className="truncate">{a.art_code}</span>
        </div>
        <div className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 mb-2 min-h-[2.4em]">{a.omschrijving}</div>
        <div className="mt-auto flex flex-col gap-1.5">
          <div className="flex items-baseline gap-1 flex-wrap">
            <span className="text-sm font-bold shop-text-primary">{fmt(a.prijs)}</span>
            {a.is_pack && (
              <span className="text-[10px] text-gray-500 font-mono">/ pack</span>
            )}
          </div>
          {a.is_pack && (
            <div className="text-[10px] text-gray-500 font-mono leading-tight" data-testid={`shop-pack-info-${a.art_code}`}>
              {a.pack_grootte} stuks × {fmt(a.prijs_per_stuk)} / stuk
            </div>
          )}
          {qty === 0 ? (
            <button className="shop-bg-primary  text-white font-bold flex items-center justify-center" style={{ height: 40 }} onClick={() => onAdd(a)} data-testid={`shop-add-${a.art_code}`}>
              <Plus size={16} weight="bold" />
            </button>
          ) : (
            <div className="flex items-center justify-between gap-1">
              <button className="bg-gray-100 hover:bg-gray-200 font-bold flex items-center justify-center flex-1" style={{ height: 40 }} onClick={() => onMin(a)} data-testid={`shop-min-${a.art_code}`}>
                <Minus size={14} weight="bold" />
              </button>
              <span className="font-bold text-base w-10 text-center">{qty}</span>
              <button className="shop-bg-primary  text-white font-bold flex items-center justify-center flex-1" style={{ height: 40 }} onClick={() => onAdd(a)} data-testid={`shop-addmore-${a.art_code}`}>
                <Plus size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ShopCatalogusPage() {
  const [data, setData] = useState({ artikels: [], categorieen: [] });
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem('shop_cart') || '[]'));

  useEffect(() => {
    API.get('/shop/artikels', { params: { q, categorie: cat } })
      .then((r) => setData(r.data))
      .catch(() => setData({ artikels: [], categorieen: [] }));
  }, [q, cat]);

  useEffect(() => {
    localStorage.setItem('shop_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('shop_cart_change'));
  }, [cart]);

  const qtyOf = (id) => cart.find((x) => x.art_id === id)?.aantal || 0;

  const onAdd = (a) => {
    setCart((c) => {
      const idx = c.findIndex((x) => x.art_id === a.id);
      if (idx >= 0) {
        const n = [...c]; n[idx] = { ...n[idx], aantal: Number(n[idx].aantal || 0) + 1 }; return n;
      }
      return [...c, { art_id: a.id, art_code: a.art_code, omschrijving: a.omschrijving, prijs: a.prijs, aantal: 1, foto_url: a.foto_url }];
    });
  };
  const onMin = (a) => {
    setCart((c) => c.map((x) => x.art_id === a.id ? { ...x, aantal: Math.max(0, x.aantal - 1) } : x).filter((x) => x.aantal > 0));
  };

  const totaal = useMemo(() => cart.reduce((s, x) => s + Number(x.prijs || 0) * Number(x.aantal || 0), 0), [cart]);

  return (
    <div data-testid="shop-bestellen-page">
      <div className="bg-white border border-gray-200 p-3 mb-3 flex flex-wrap items-center gap-2 sticky top-[68px] z-20">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full border border-gray-200 pl-9 pr-3 py-2 text-sm focus:shop-border-primary outline-none" placeholder="Zoek op naam, artikelcode of EAN…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="shop-search" />
        </div>
        <select className="border border-gray-200 px-3 py-2 text-sm" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="shop-cat-filter">
          <option value="">Alle categorieën</option>
          {data.categorieen.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="text-xs text-gray-500">{data.artikels.length} artikels</div>
      </div>

      {data.artikels.length === 0 ? (
        <div className="text-center text-gray-400 py-12">Geen artikels gevonden voor uw zoekopdracht.</div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
          {data.artikels.map((a) => (
            <Kaart key={a.id} a={a} qty={qtyOf(a.id)} onAdd={onAdd} onMin={onMin} />
          ))}
        </div>
      )}

      {/* Sticky totaal-balk onderaan */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 shop-border-primary shadow-xl z-30">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-sm">
              <strong>{cart.length}</strong> artikels in mandje · Totaal: <strong className="shop-text-primary">{fmt(totaal)}</strong>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setCart([])} className="text-xs text-gray-500 hover:text-red-500 inline-flex items-center gap-1" data-testid="shop-clear-cart">
                <X size={12} /> Leegmaken
              </button>
              <a href="/shop/mandje" className="shop-bg-primary  text-white font-bold px-4 py-2 text-sm" data-testid="shop-naar-mandje">
                Naar mandje →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
